import { useTranslations } from "use-intl";
import { useThreadPresence } from "@/app/Threads/Components/ThreadPresenceProvider";
import { threadRecordQuery } from "@/app/Threads/GraphQL/threadQueries";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { ThreadUserAvatar } from "@/app/Threads/Components/ThreadUserAvatar";
import { type ThreadMember } from "@/app/Threads/hooks/useThreadPresenceChannel";

const DOT_DELAYS_MS = [0, 150, 300];

export function TypingIndicator({ threadId }: { threadId: string }) {
  const presence = useThreadPresence();
  const typingMembers = presence?.typingMembers ?? [];

  if (typingMembers.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 space-y-4">
      {typingMembers.map((member) => (
        <TypingBubble key={member.id} threadId={threadId} member={member} />
      ))}
    </div>
  );
}

function TypingBubble({
  threadId,
  member,
}: {
  threadId: string;
  member: ThreadMember;
}) {
  const t = useTranslations("thread.presence");
  const { data } = useGqlQuery(threadRecordQuery, { id: threadId });
  const subscriber = data?.thread?.subscribers.find(
    (s) => s.user.id === member.id,
  );
  const name = member.name ?? "";

  return (
    <div className="flex items-end gap-2">
      <ThreadUserAvatar
        className="size-8"
        name={member.name}
        media={subscriber?.user.profilePicture}
      />
      <div
        className="flex items-center gap-1 rounded-2xl bg-gray-200 px-4 py-3"
        role="status"
      >
        <span className="sr-only">
          {t("typing", { count: 1, names: name })}
        </span>
        {DOT_DELAYS_MS.map((delay) => (
          <span
            key={delay}
            className="animate-typing-dot size-2 rounded-full bg-gray-500"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </div>
    </div>
  );
}
